import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Switch,
  Paper,
} from '@mui/material';
import {
  History as HistoryIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  AccessTime as TimeIcon,
  ChevronRight as ChevronRightIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { useAuthContext } from '../../context/AuthContext';
import { useTimeFormat } from '../../context/TimeFormatContext';

interface MobileMoreProps {
  onNavigate?: (view: string) => void;
}

export const MobileMore: React.FC<MobileMoreProps> = ({ onNavigate }) => {
  const { user, logout, isInMApp } = useAuthContext();
  const { use24HourFormat, toggleTimeFormat } = useTimeFormat();

  const menuItems = [
    {
      id: 'help',
      label: 'Help Center',
      icon: <HelpIcon />,
      onClick: () => onNavigate?.('help'),
    },
    {
      id: 'activity',
      label: 'Activity Log',
      icon: <HistoryIcon />,
      onClick: () => onNavigate?.('activity'),
    },
    {
      id: 'my-activity',
      label: 'My Activity',
      icon: <PersonIcon />,
      onClick: () => onNavigate?.('my-activity'),
    },
  ];

  return (
    <Box sx={{ height: '100%', bgcolor: '#f8f9fa', pb: 4 }}>
      {/* User Header Card - Compact */}
      <Box
        sx={{
          mx: 2,
          mt: 2,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar 
            sx={{ 
              width: 44, 
              height: 44, 
              bgcolor: 'primary.main', 
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" fontWeight={600} noWrap>
              {user?.firstName} {user?.lastName}
            </Typography>
            {user?.orgDisplayName && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {user.orgDisplayName}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Combined Settings & Navigation - More Compact */}
      <Box
        sx={{
          mx: 2,
          mt: 1.5,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        <List disablePadding dense>
          {/* Time Format Setting */}
          <ListItem sx={{ py: 1, px: 2 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <TimeIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            </ListItemIcon>
            <ListItemText
              primary="24-Hour Time"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              secondary={use24HourFormat ? '14:30' : '2:30 PM'}
              secondaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.7rem' } }}
            />
            <Switch
              edge="end"
              checked={use24HourFormat}
              onChange={toggleTimeFormat}
              size="small"
              color="primary"
            />
          </ListItem>
          
          <Divider sx={{ mx: 2 }} />

          {/* Navigation Items */}
          {menuItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem disablePadding>
                <ListItemButton onClick={item.onClick} sx={{ py: 1, px: 2 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {React.cloneElement(item.icon as React.ReactElement, { 
                      sx: { fontSize: 20, color: 'text.secondary' } 
                    })}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label} 
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  />
                  <ChevronRightIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                </ListItemButton>
              </ListItem>
              {index < menuItems.length - 1 && <Divider sx={{ ml: 7, mr: 2 }} />}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Logout - only show when not inside native MApp container */}
      {!isInMApp && (
        <Box
          sx={{
            mx: 2,
            mt: 1.5,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          <List disablePadding dense>
            <ListItem disablePadding>
              <ListItemButton onClick={logout} sx={{ py: 1, px: 2 }}>
                <ListItemIcon sx={{ minWidth: 36, color: 'error.main' }}>
                  <LogoutIcon sx={{ fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Log Out" 
                  primaryTypographyProps={{ 
                    variant: 'body2',
                    color: 'error.main',
                    fontWeight: 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      )}

      {/* Version - Compact */}
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ display: 'block', textAlign: 'center', mt: 3, fontSize: '0.65rem' }}
      >
        Byte Store Control v1.0
      </Typography>
      
      {/* Bottom spacer */}
      <Box sx={{ height: 16 }} />
    </Box>
  );
};
